import { CardHeader, Typography } from '@mui/material'
import { ReactElement } from 'react'
import { useParams } from 'react-router'
import { Feature_Base_Bool_Exp, Feature_Sorting_Config_Bool_Exp } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  fetchKapMeasureByIdWithFeaturesQuery,
  fetchKapMeasureShortTitleAndLevelByIdQuery,
} from 'src/screens/kap/program/details/measures/kapMeasuresQueries'
import { KapMeasureFeaturesKpiGrid } from 'src/screens/kap/program/details/measures/measure/features/KapMeasureFeaturesKpiGrid'
import { KapMeasureFeaturesYearlyKpiGrid } from 'src/screens/kap/shared/measure/KapMeasureFeaturesYearlyKpiGrid'
import { FeatureBase } from 'src/screens/shared/feature/FeatureBase'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { LEVEL_TYPE } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { ExpandableCard } from 'src/shared/presentation/ExpandableCard'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { useQuery } from 'urql'

export const KapMeasureFeaturesPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const { programId, measureId } = useParams()
  const {
    canEditMeasures,
    loading,
    metadata: { dossierStatus },
  } = usePermissionsForKapProgram(parseInt(programId as string))
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [{ data, error, fetching }] = useQuery({
    query: fetchKapMeasureByIdWithFeaturesQuery,
    variables: { measureId: measureId },
  })

  const [{ data: measureShortTitleData, error: measureShortTitleError }] = useQuery({
    query: fetchKapMeasureShortTitleAndLevelByIdQuery,
    variables: { measureId: measureId },
  })

  if (error || measureShortTitleError) {
    notificationService.operationFailed()
  }

  const kapMeasure = data?.kap_measure_by_pk
  const pageTitle = measureShortTitleData
    ? `${getMessage('label.measure')}: ${measureShortTitleData.kap_measure_by_pk?.short_title}`
    : ''

  const measuresBaseWhereClause: Feature_Base_Bool_Exp = {
    kap_measures: {
      id: {
        _eq: parseInt(measureId as string),
      },
    },
  }

  const featureConfigSortingWhere: Feature_Sorting_Config_Bool_Exp = {
    level: {
      _eq: kapMeasure?.level,
    },
  }

  const handleEditFeatureTypeSection = (featureTypeId: number) => {
    navigate(ROUTES.KapMeasureDetailsRoot.nested.FeaturesEdit.params({ programId, measureId, featureTypeId }))
  }

  const backHandler = () => {
    navigate(ROUTES.KapDetailsRoot.nested.Measures.params({ programId }))
  }

  return (
    <>
      <ScreenLayout title={pageTitle} onBack={backHandler}>
        <PageLayout>
          {!loading && !fetching && (
            <>
              <FeatureBase
                featureBaseWhere={measuresBaseWhereClause}
                featureSortingConfigWhere={featureConfigSortingWhere}
                canEdit={canEditMeasures}
                onEditFeature={handleEditFeatureTypeSection}
                kpiSection={
                  <>
                    <Section id="measure-features" async title={getMessage('label.program.kpis')}>
                      <KapMeasureFeaturesKpiGrid measureLevel={kapMeasure.level} />
                    </Section>
                    {dossierStatus && Utils.isDossierStatusAfterOrImplementation(dossierStatus) && (
                      <Section
                        id="measure-features-per-year"
                        title={getMessage('label.kap.measure.kpi.report.per.year.title')}
                        helpAndInstructions={
                          <HelpAndInstructions
                            labelKey={'label.kap.measure.help.and.instructions.kpi.report.per.year'}
                          />
                        }
                      >
                        <ExpandableCard
                          header={
                            <CardHeader
                              title={
                                <Typography variant="h6">
                                  {getMessage('label.kap.measure.kpi.report.per.year.title')}
                                </Typography>
                              }
                            />
                          }
                          content={
                            <KapMeasureFeaturesYearlyKpiGrid
                              measureId={parseInt(measureId!)}
                              measureLevel={kapMeasure?.level as LEVEL_TYPE}
                            />
                          }
                        />
                      </Section>
                    )}
                  </>
                }
                relatedEntity={kapMeasure}
              />
            </>
          )}
        </PageLayout>
      </ScreenLayout>
    </>
  )
}
